import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { SignalRService, signalRService } from '../services/signalRService';
import { 
  ConnectionState, 
  SignalREventHandlers, 
  WorkStatusEvent, 
  BreakStatusEvent, 
  TeamUpdateEvent, 
  UserStatusEvent, 
  SystemNotificationEvent, 
  AlertEvent, 
  CommentEvent,
  MaterialConsumptionEvent 
} from '../types/signalRTypes';

// Real-time state interface
interface RealTimeState {
  connectionState: ConnectionState;
  isConnected: boolean;
  connectionId: string | null;
  userId: string | null;
  userRole: string | null;
  lastActivity: Date | null;
  error: Error | null;
  
  // Event data
  workEvents: WorkStatusEvent[];
  breakEvents: BreakStatusEvent[];
  teamEvents: TeamUpdateEvent[];
  userEvents: UserStatusEvent[];
  notifications: SystemNotificationEvent[];
  alerts: AlertEvent[];
  comments: CommentEvent[];
  materialEvents: MaterialConsumptionEvent[];
}

// Initial state
const initialState: RealTimeState = {
  connectionState: 'Disconnected',
  isConnected: false,
  connectionId: null,
  userId: null,
  userRole: null,
  lastActivity: null,
  error: null,
  workEvents: [],
  breakEvents: [],
  teamEvents: [],
  userEvents: [],
  notifications: [],
  alerts: [],
  comments: [],
  materialEvents: []
};

// Action types
type RealTimeAction = 
  | { type: 'SET_CONNECTION_STATE'; payload: ConnectionState }
  | { type: 'SET_CONNECTION_INFO'; payload: { connectionId: string; userId: string; userRole: string } }
  | { type: 'SET_ERROR'; payload: Error | null }
  | { type: 'ADD_WORK_EVENT'; payload: WorkStatusEvent }
  | { type: 'ADD_BREAK_EVENT'; payload: BreakStatusEvent }
  | { type: 'ADD_TEAM_EVENT'; payload: TeamUpdateEvent }
  | { type: 'ADD_USER_EVENT'; payload: UserStatusEvent }
  | { type: 'ADD_NOTIFICATION'; payload: SystemNotificationEvent }
  | { type: 'ADD_ALERT'; payload: AlertEvent }
  | { type: 'ADD_COMMENT'; payload: CommentEvent }
  | { type: 'ADD_MATERIAL_EVENT'; payload: MaterialConsumptionEvent }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'CLEAR_ALERTS' }
  | { type: 'CLEAR_EVENTS' }
  | { type: 'DISCONNECT' };

// Reducer
const realTimeReducer = (state: RealTimeState, action: RealTimeAction): RealTimeState => {
  switch (action.type) {
    case 'SET_CONNECTION_STATE':
      return {
        ...state,
        connectionState: action.payload,
        isConnected: action.payload === 'Connected',
        lastActivity: action.payload === 'Connected' ? new Date() : state.lastActivity
      };
      
    case 'SET_CONNECTION_INFO':
      return {
        ...state,
        connectionId: action.payload.connectionId,
        userId: action.payload.userId,
        userRole: action.payload.userRole
      };
      
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };
      
    case 'ADD_WORK_EVENT':
      const currentWorkEvents = state.workEvents || [];
      return {
        ...state,
        workEvents: [action.payload, ...currentWorkEvents.slice(0, 99)], // Keep last 100 events
        lastActivity: new Date()
      };
      
    case 'ADD_BREAK_EVENT':
      const currentBreakEvents = state.breakEvents || [];
      return {
        ...state,
        breakEvents: [action.payload, ...currentBreakEvents.slice(0, 99)],
        lastActivity: new Date()
      };
      
    case 'ADD_TEAM_EVENT':
      const currentTeamEvents = state.teamEvents || [];
      return {
        ...state,
        teamEvents: [action.payload, ...currentTeamEvents.slice(0, 99)],
        lastActivity: new Date()
      };
      
    case 'ADD_USER_EVENT':
      const currentUserEvents = state.userEvents || [];
      return {
        ...state,
        userEvents: [action.payload, ...currentUserEvents.slice(0, 99)],
        lastActivity: new Date()
      };
      
    case 'ADD_NOTIFICATION':
      const currentNotifications = state.notifications || [];
      return {
        ...state,
        notifications: [action.payload, ...currentNotifications.slice(0, 49)], // Keep last 50 notifications
        lastActivity: new Date()
      };
      
    case 'ADD_ALERT':
      const currentAlerts = state.alerts || [];
      return {
        ...state,
        alerts: [action.payload, ...currentAlerts.slice(0, 19)], // Keep last 20 alerts
        lastActivity: new Date()
      };
      
    case 'ADD_COMMENT':
      const currentComments = state.comments || [];
      return {
        ...state,
        comments: [action.payload, ...currentComments.slice(0, 99)],
        lastActivity: new Date()
      };
      
    case 'ADD_MATERIAL_EVENT':
      const currentMaterialEvents = state.materialEvents || [];
      return {
        ...state,
        materialEvents: [action.payload, ...currentMaterialEvents.slice(0, 99)], // Keep last 100 events
        lastActivity: new Date()
      };
      
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: []
      };
      
    case 'CLEAR_ALERTS':
      return {
        ...state,
        alerts: []
      };
      
    case 'CLEAR_EVENTS':
      return {
        ...state,
        workEvents: [],
        breakEvents: [],
        teamEvents: [],
        userEvents: [],
        comments: [],
        materialEvents: []
      };
      
    case 'DISCONNECT':
      return {
        ...initialState,
        error: state.error
      };
      
    default:
      return state;
  }
};

// Context interface
interface RealTimeContextType {
  // State
  state: RealTimeState;
  
  // Actions
  connect: (userId: string, userRole: string) => Promise<void>;
  disconnect: () => Promise<void>;
  joinTeamGroup: (teamId: string) => Promise<void>;
  leaveTeamGroup: (teamId: string) => Promise<void>;
  joinProjectGroup: (projectId: string) => Promise<void>;
  leaveProjectGroup: (projectId: string) => Promise<void>;
  ping: () => Promise<void>;
  
  // Utility functions
  clearNotifications: () => void;
  clearAlerts: () => void;
  clearEvents: () => void;
  
  // Event subscriptions
  subscribeToWorkEvents: (handler: (event: WorkStatusEvent) => void) => void;
  subscribeToBreakEvents: (handler: (event: BreakStatusEvent) => void) => void;
  subscribeToTeamEvents: (handler: (event: TeamUpdateEvent) => void) => void;
  subscribeToUserEvents: (handler: (event: UserStatusEvent) => void) => void;
  subscribeToNotifications: (handler: (event: SystemNotificationEvent) => void) => void;
  subscribeToAlerts: (handler: (event: AlertEvent) => void) => void;
  subscribeToComments: (handler: (event: CommentEvent) => void) => void;
  subscribeToMaterialEvents: (handler: (event: MaterialConsumptionEvent) => void) => void;
  subscribeToCustomEvent: (eventName: string, handler: (event: any) => void) => () => void;
  
  // Individual work event subscriptions (for compatibility)
  subscribeToWorkStarted: (handler: (event: WorkStatusEvent) => void) => () => void;
  subscribeToWorkEnded: (handler: (event: WorkStatusEvent) => void) => () => void;
  subscribeToTeamMemberStatusChanged: (handler: (event: TeamUpdateEvent) => void) => () => void;
}

// Create context
const RealTimeContext = createContext<RealTimeContextType | undefined>(undefined);

// Provider component
export const RealTimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(realTimeReducer, initialState);
  
  // Event handlers
  const eventHandlers: SignalREventHandlers = {
    onWorkStarted: (event) => dispatch({ type: 'ADD_WORK_EVENT', payload: event }),
    onWorkEnded: (event) => dispatch({ type: 'ADD_WORK_EVENT', payload: event }),
    onWorkUpdated: (event) => dispatch({ type: 'ADD_WORK_EVENT', payload: event }),
    
    onBreakStarted: (event) => dispatch({ type: 'ADD_BREAK_EVENT', payload: event }),
    onBreakEnded: (event) => dispatch({ type: 'ADD_BREAK_EVENT', payload: event }),
    onBreakUpdated: (event) => dispatch({ type: 'ADD_BREAK_EVENT', payload: event }),
    
    onTeamMemberStatusChanged: (event) => dispatch({ type: 'ADD_TEAM_EVENT', payload: event }),
    onTeamCompositionChanged: (event) => dispatch({ type: 'ADD_TEAM_EVENT', payload: event }),
    onTeamPerformanceUpdated: (event) => dispatch({ type: 'ADD_TEAM_EVENT', payload: event }),
    
    onUserStatusChanged: (event) => dispatch({ type: 'ADD_USER_EVENT', payload: event }),
    onUserCreated: (event) => dispatch({ type: 'ADD_USER_EVENT', payload: event }),
    onUserUpdated: (event) => dispatch({ type: 'ADD_USER_EVENT', payload: event }),
    onUserDeleted: (event) => dispatch({ type: 'ADD_USER_EVENT', payload: { userId: event.id, status: 'deleted', data: event, timestamp: event.timestamp } }),
    
    onSystemNotification: (event) => dispatch({ type: 'ADD_NOTIFICATION', payload: event }),
    onAlertTriggered: (event) => dispatch({ type: 'ADD_ALERT', payload: event }),
    onCommentAdded: (event) => dispatch({ type: 'ADD_COMMENT', payload: event }),
    
    onMaterialConsumptionCreated: (event) => dispatch({ type: 'ADD_MATERIAL_EVENT', payload: event }),
    onMaterialConsumptionUpdated: (event) => dispatch({ type: 'ADD_MATERIAL_EVENT', payload: event }),
    onMaterialConsumptionDeleted: (event) => dispatch({ type: 'ADD_MATERIAL_EVENT', payload: event }),
    
    onConnectionStatusChanged: (event) => {
      dispatch({ type: 'SET_CONNECTION_STATE', payload: event.status as ConnectionState });
    },
    
    onForceDisconnect: (reason) => {
      console.warn('SignalR: Force disconnect:', reason);
      dispatch({ type: 'DISCONNECT' });
      dispatch({ type: 'SET_ERROR', payload: new Error(`Force disconnect: ${reason}`) });
    }
  };
  
  // Set up SignalR event handlers
  useEffect(() => {
    signalRService.setEventHandlers(eventHandlers);
    
    signalRService.setConnectionStateListener((connectionState) => {
      dispatch({ type: 'SET_CONNECTION_STATE', payload: connectionState });
    });
    
    signalRService.setErrorListener((error) => {
      dispatch({ type: 'SET_ERROR', payload: error });
    });
    
    return () => {
      // Clean up on unmount
      signalRService.setEventHandlers({});
      signalRService.setConnectionStateListener(() => {});
      signalRService.setErrorListener(() => {});
    };
  }, []);
  
  // Connection functions
  const connect = useCallback(async (userId: string, userRole: string) => {
    try {
      console.log('RealTimeContext: Attempting to connect SignalR for user', userId, 'with role', userRole);
      dispatch({ type: 'SET_ERROR', payload: null });
      
      // Check if SignalR should be used in this environment
      if (!signalRService.shouldUseSignalR()) {
        console.log('SignalR: Disabled in this environment');
        dispatch({ type: 'SET_CONNECTION_STATE', payload: 'Disconnected' });
        return;
      }
      
      await signalRService.connect(userId, userRole);
      
      const connectionInfo = signalRService.getConnectionInfo();
      if (connectionInfo) {
        dispatch({ 
          type: 'SET_CONNECTION_INFO', 
          payload: { 
            connectionId: connectionInfo.connectionId, 
            userId: connectionInfo.userId, 
            userRole: connectionInfo.role 
          } 
        });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      
      // In production, don't throw errors to prevent app crashes
      if (import.meta.env.PROD) {
        console.warn('SignalR: Connection failed in production, continuing without real-time updates');
        return;
      }
      
      throw error;
    }
  }, []);
  
  const disconnect = useCallback(async () => {
    try {
      await signalRService.disconnect();
      dispatch({ type: 'DISCONNECT' });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      throw error;
    }
  }, []);
  
  const joinTeamGroup = useCallback(async (teamId: string) => {
    try {
      if (!signalRService.isConnected()) {
        console.warn('SignalR: Not connected, skipping joinTeamGroup');
        return;
      }
      await signalRService.joinTeamGroup(teamId);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      if (import.meta.env.DEV) {
        throw error;
      }
    }
  }, []);
  
  const leaveTeamGroup = useCallback(async (teamId: string) => {
    try {
      if (!signalRService.isConnected()) {
        console.warn('SignalR: Not connected, skipping leaveTeamGroup');
        return;
      }
      await signalRService.leaveTeamGroup(teamId);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      if (import.meta.env.DEV) {
        throw error;
      }
    }
  }, []);
  
  const joinProjectGroup = useCallback(async (projectId: string) => {
    try {
      if (!signalRService.isConnected()) {
        console.warn('SignalR: Not connected, skipping joinProjectGroup');
        return;
      }
      await signalRService.joinProjectGroup(projectId);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      if (import.meta.env.DEV) {
        throw error;
      }
    }
  }, []);
  
  const leaveProjectGroup = useCallback(async (projectId: string) => {
    try {
      if (!signalRService.isConnected()) {
        console.warn('SignalR: Not connected, skipping leaveProjectGroup');
        return;
      }
      await signalRService.leaveProjectGroup(projectId);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      if (import.meta.env.DEV) {
        throw error;
      }
    }
  }, []);
  
  const ping = useCallback(async () => {
    try {
      if (!signalRService.isConnected()) {
        console.warn('SignalR: Not connected, skipping ping');
        return;
      }
      await signalRService.ping();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      if (import.meta.env.DEV) {
        throw error;
      }
    }
  }, []);
  
  // Utility functions
  const clearNotifications = useCallback(() => {
    dispatch({ type: 'CLEAR_NOTIFICATIONS' });
  }, []);
  
  const clearAlerts = useCallback(() => {
    dispatch({ type: 'CLEAR_ALERTS' });
  }, []);
  
  const clearEvents = useCallback(() => {
    dispatch({ type: 'CLEAR_EVENTS' });
  }, []);
  
  // Event subscription functions (for custom handlers)
  const subscribeToWorkEvents = useCallback((handler: (event: WorkStatusEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onWorkStarted: handler,
      onWorkEnded: handler,
      onWorkUpdated: handler
    });
  }, []);
  
  const subscribeToBreakEvents = useCallback((handler: (event: BreakStatusEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onBreakStarted: handler,
      onBreakEnded: handler,
      onBreakUpdated: handler
    });
  }, []);
  
  const subscribeToTeamEvents = useCallback((handler: (event: TeamUpdateEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onTeamMemberStatusChanged: handler,
      onTeamCompositionChanged: handler,
      onTeamPerformanceUpdated: handler
    });
  }, []);
  
  const subscribeToUserEvents = useCallback((handler: (event: UserStatusEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onUserStatusChanged: handler,
      onUserCreated: handler,
      onUserUpdated: handler
    });
  }, []);
  
  const subscribeToNotifications = useCallback((handler: (event: SystemNotificationEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onSystemNotification: handler
    });
  }, []);
  
  const subscribeToAlerts = useCallback((handler: (event: AlertEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onAlertTriggered: handler
    });
  }, []);
  
  const subscribeToComments = useCallback((handler: (event: CommentEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onCommentAdded: handler
    });
  }, []);
  
  const subscribeToMaterialEvents = useCallback((handler: (event: MaterialConsumptionEvent) => void) => {
    const currentHandlers = signalRService['eventHandlers'] || {};
    signalRService.setEventHandlers({
      ...currentHandlers,
      onMaterialConsumptionCreated: handler,
      onMaterialConsumptionUpdated: handler,
      onMaterialConsumptionDeleted: handler
    });
  }, []);
  
  const subscribeToCustomEvent = useCallback((eventName: string, handler: (event: any) => void) => {
    if (!signalRService.isConnected()) {
      console.warn(`SignalR not connected, cannot subscribe to ${eventName}`);
      return () => {};
    }
    
    // Store the handler for this custom event
    const customHandlers = new Map<string, (event: any) => void>();
    customHandlers.set(eventName, handler);
    
    // Register handler with SignalR
    signalRService.connection?.on(eventName, handler);
    
    // Return unsubscribe function
    return () => {
      if (signalRService.connection) {
        signalRService.connection.off(eventName, handler);
      }
      customHandlers.delete(eventName);
    };
  }, []);

  // Individual subscription methods for specific events
  const subscribeToWorkStarted = useCallback((handler: (event: WorkStatusEvent) => void) => {
    // Store the handler reference for cleanup
    const wrappedHandler = (event: WorkStatusEvent) => {
      console.log('WorkStarted event received in subscription:', event);
      handler(event);
    };
    
    // Set up the handler
    const currentHandlers = { ...eventHandlers };
    const existingOnWorkStarted = currentHandlers.onWorkStarted;
    
    currentHandlers.onWorkStarted = (event: WorkStatusEvent) => {
      // Call the base handler first (to update state)
      if (existingOnWorkStarted) {
        existingOnWorkStarted(event);
      }
      // Then call the custom handler
      wrappedHandler(event);
    };
    
    signalRService.setEventHandlers(currentHandlers);
    
    // Return cleanup function
    return () => {
      // Restore original handler
      signalRService.setEventHandlers(eventHandlers);
    };
  }, [eventHandlers]);
  
  const subscribeToWorkEnded = useCallback((handler: (event: WorkStatusEvent) => void) => {
    // Store the handler reference for cleanup
    const wrappedHandler = (event: WorkStatusEvent) => {
      console.log('WorkEnded event received in subscription:', event);
      handler(event);
    };
    
    // Set up the handler
    const currentHandlers = { ...eventHandlers };
    const existingOnWorkEnded = currentHandlers.onWorkEnded;
    
    currentHandlers.onWorkEnded = (event: WorkStatusEvent) => {
      // Call the base handler first (to update state)
      if (existingOnWorkEnded) {
        existingOnWorkEnded(event);
      }
      // Then call the custom handler
      wrappedHandler(event);
    };
    
    signalRService.setEventHandlers(currentHandlers);
    
    // Return cleanup function
    return () => {
      // Restore original handler
      signalRService.setEventHandlers(eventHandlers);
    };
  }, [eventHandlers]);
  
  const subscribeToTeamMemberStatusChanged = useCallback((handler: (event: TeamUpdateEvent) => void) => {
    // Store the handler reference for cleanup
    const wrappedHandler = (event: TeamUpdateEvent) => {
      console.log('TeamMemberStatusChanged event received in subscription:', event);
      handler(event);
    };
    
    // Set up the handler
    const currentHandlers = { ...eventHandlers };
    const existingOnTeamMemberStatusChanged = currentHandlers.onTeamMemberStatusChanged;
    
    currentHandlers.onTeamMemberStatusChanged = (event: TeamUpdateEvent) => {
      // Call the base handler first (to update state)
      if (existingOnTeamMemberStatusChanged) {
        existingOnTeamMemberStatusChanged(event);
      }
      // Then call the custom handler
      wrappedHandler(event);
    };
    
    signalRService.setEventHandlers(currentHandlers);
    
    // Return cleanup function
    return () => {
      // Restore original handler
      signalRService.setEventHandlers(eventHandlers);
    };
  }, [eventHandlers]);
  
  const contextValue: RealTimeContextType = {
    state,
    connect,
    disconnect,
    joinTeamGroup,
    leaveTeamGroup,
    joinProjectGroup,
    leaveProjectGroup,
    ping,
    clearNotifications,
    clearAlerts,
    clearEvents,
    subscribeToWorkEvents,
    subscribeToBreakEvents,
    subscribeToTeamEvents,
    subscribeToUserEvents,
    subscribeToNotifications,
    subscribeToAlerts,
    subscribeToComments,
    subscribeToMaterialEvents,
    subscribeToCustomEvent,
    subscribeToWorkStarted,
    subscribeToWorkEnded,
    subscribeToTeamMemberStatusChanged
  };
  
  return (
    <RealTimeContext.Provider value={contextValue}>
      {children}
    </RealTimeContext.Provider>
  );
};

// Custom hook to use real-time context
export const useRealTime = (): RealTimeContextType => {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error('useRealTime must be used within a RealTimeProvider');
  }
  return context;
};

// Export context for advanced usage
export { RealTimeContext };